import { Link } from "react-router-dom";
import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useCategories } from "../lib/categoriesContext";
import { usePageContent, show } from "../lib/usePageContent";
import { Logo } from "./Logo";
import { Facebook, Instagram, Linkedin, Youtube, MessageCircle, Send } from "lucide-react";

export function Footer() {
  const { content } = usePageContent("footer");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "", msg: "" });
  const { categories } = useCategories();

  const subscribe = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      const { data } = await api.post("/newsletter", { email });
      setStatus({
        type: "ok",
        msg: data.already
          ? content.newsletter_already_message
          : content.newsletter_success_message,
      });
      setEmail("");
    } catch (err) {
      setStatus({ type: "err", msg: formatApiError(err.response?.data?.detail) || "Erreur" });
    }
  };

  // Render the multi-line newsletter title with paragraph breaks (one per line)
  // Hashtags: split on whitespace, keep only those starting with '#'
  const hashtags = (content.hashtags || "")
    .split(/\s+/)
    .filter((h) => h.startsWith("#"));

  return (
    <footer className="bg-[#1A1A18] text-[#FAF8F3] mt-24" data-testid="site-footer">
      <section className="border-b border-[#FAF8F3]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            {show(content, "newsletter_label") && content.newsletter_label && (
              <div className="font-mono-label text-[#C4944A] mb-3">{content.newsletter_label}</div>
            )}
            {show(content, "newsletter_title") && content.newsletter_title && (
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mb-3 whitespace-pre-line">
                {content.newsletter_title}
              </h2>
            )}
            {show(content, "newsletter_description") && content.newsletter_description && (
              <p className="text-[#FAF8F3]/70 max-w-md whitespace-pre-line">{content.newsletter_description}</p>
            )}
          </div>
          <form onSubmit={subscribe} className="flex flex-col gap-4" data-testid="footer-newsletter-form">
            <div className="flex items-center border-b-2 border-[#FAF8F3] pb-2 gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={content.newsletter_email_placeholder || ""}
                className="bg-transparent text-lg w-full outline-none placeholder:text-[#FAF8F3]/40"
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                className="font-mono-label flex items-center gap-2 text-[#C4944A] hover:text-[#FAF8F3]"
                data-testid="footer-newsletter-submit"
              >
                {content.newsletter_button_label || "Je m'inscris"} <Send size={14} />
              </button>
            </div>
            {status.msg && (
              <div
                className={`font-mono-label ${status.type === "err" ? "text-red-300" : "text-[#8FAF82]"}`}
                data-testid="footer-newsletter-status"
              >
                {status.msg}
              </div>
            )}
            {show(content, "newsletter_disclaimer") && content.newsletter_disclaimer && (
              <p className="text-xs text-[#FAF8F3]/50">{content.newsletter_disclaimer}</p>
            )}
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo size="lg" inverted stacked />
          <p className="mt-5 max-w-md text-[#FAF8F3]/70">
            {content.brand_description}<br />
            <span className="font-quote text-[#C4944A]">{content.brand_quote}</span>
          </p>
          {(content.address || content.email || content.phone) && (
            <div className="mt-5 text-sm text-[#FAF8F3]/60 space-y-1" data-testid="footer-contact-info">
              {content.address && <div className="whitespace-pre-line">{content.address}</div>}
              {content.email && (
                <div>
                  <a href={`mailto:${content.email}`} className="hover:text-[#C4944A]" data-testid="footer-email">
                    {content.email}
                  </a>
                </div>
              )}
              {content.phone && (
                <div>
                  <a href={`tel:${content.phone.replace(/\s/g, "")}`} className="hover:text-[#C4944A]" data-testid="footer-phone">
                    {content.phone}
                  </a>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-4 mt-6 flex-wrap">
            {content.social_facebook && (
              <a href={content.social_facebook} target="_blank" rel="noopener noreferrer" className="border border-[#FAF8F3]/30 p-3 hover:border-[#C4944A] hover:text-[#C4944A]" aria-label="Facebook" data-testid="footer-facebook">
                <Facebook size={18} />
              </a>
            )}
            {content.social_instagram && (
              <a href={content.social_instagram} target="_blank" rel="noopener noreferrer" className="border border-[#FAF8F3]/30 p-3 hover:border-[#C4944A] hover:text-[#C4944A]" aria-label="Instagram" data-testid="footer-instagram">
                <Instagram size={18} />
              </a>
            )}
            {content.social_linkedin && (
              <a href={content.social_linkedin} target="_blank" rel="noopener noreferrer" className="border border-[#FAF8F3]/30 p-3 hover:border-[#C4944A] hover:text-[#C4944A]" aria-label="LinkedIn" data-testid="footer-linkedin">
                <Linkedin size={18} />
              </a>
            )}
            {content.social_youtube && (
              <a href={content.social_youtube} target="_blank" rel="noopener noreferrer" className="border border-[#FAF8F3]/30 p-3 hover:border-[#C4944A] hover:text-[#C4944A]" aria-label="YouTube" data-testid="footer-youtube">
                <Youtube size={18} />
              </a>
            )}
            {content.social_whatsapp && (
              <a href={content.social_whatsapp} target="_blank" rel="noopener noreferrer" className="border border-[#FAF8F3]/30 p-3 hover:border-[#C4944A] hover:text-[#C4944A]" aria-label="WhatsApp" data-testid="footer-whatsapp">
                <MessageCircle size={18} />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="font-mono-label text-[#C4944A] mb-4">{content.rubriques_label}</div>
          <ul className="grid grid-cols-1 gap-1.5 text-sm">
            {categories.slice(0, 8).map((c) => (
              <li key={c.slug}>
                <Link to={`/rubrique/${c.slug}`} className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid={`footer-cat-${c.slug}`}>
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="font-mono-label text-[#C4944A] mb-4">{content.movement_label}</div>
          <ul className="grid grid-cols-1 gap-1.5 text-sm">
            <li><Link to="/plaidoyer" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-plaidoyer">Notre Plaidoyer</Link></li>
            <li><Link to="/ressources" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-ressources">Ressources</Link></li>
            <li><Link to="/boutique" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-boutique">Boutique</Link></li>
            <li><Link to="/a-propos" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-about">À propos</Link></li>
            <li><Link to="/contact" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-contact">Contact</Link></li>
            <li><Link to="/recherche" className="text-[#FAF8F3]/80 hover:text-[#FAF8F3]" data-testid="footer-search">Recherche</Link></li>
            <li><Link to="/admin" className="text-[#FAF8F3]/50 hover:text-[#FAF8F3]" data-testid="footer-admin">Espace rédaction</Link></li>
          </ul>
        </div>
      </section>

      <section className="border-t border-[#FAF8F3]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-3 gap-6 text-xs">
          <div className="md:col-span-2 text-[#FAF8F3]/60 leading-relaxed" data-testid="legal-disclaimer">
            <span className="font-mono-label text-[#C4944A] block mb-2">{content.legal_label}</span>
            {content.legal_text}
          </div>
          {hashtags.length > 0 && (
            <div className="text-[#FAF8F3]/40 font-mono-label flex flex-wrap gap-x-3 gap-y-1 md:justify-end">
              {hashtags.map((h, i) => (
                <span key={i}>{h}</span>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-[#FAF8F3]/10 py-4 text-center text-[#FAF8F3]/40 font-mono-label text-[10px]">
          {content.copyright}
        </div>
      </section>
    </footer>
  );
}
